// Display-only conversion: original prices remain the values used by edit forms.
export const EVENT_CURRENCIES = {
  New_York: 'USD', North_America: 'USD', Greece: 'EUR', World: 'EUR', London: 'GBP',
};
export const APPROVED_CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'PLN'];
const SUPPORTED = new Set(APPROVED_CURRENCIES);

// Currency of a position's adjustments: they are entered in the original purchase currency.
// Returns { currency, assumed } or { currency: null } when the purchases disagree.
export function adjustmentOriginalCurrency(units, assumedCurrency) {
  const currencies = new Set((units || [])
    .filter((unit) => unit.purchase_id != null && unit.unit_price != null)
    .map((unit) => {
      const code = String(unit.purchase_currency || '').trim().toUpperCase();
      const reason = unit.purchase_currency_review_reason;
      return !code && (!reason || reason === 'missing') ? assumedCurrency || null : code || null;
    }));
  if (currencies.size > 1 || currencies.has(null)) return { currency: null, assumed: false };
  if (currencies.size === 1) return { currency: [...currencies][0], assumed: false };
  return { currency: assumedCurrency || null, assumed: Boolean(assumedCurrency) };
}

// Keep each side in its recorded currency; never net different currencies.
// adjustments: [{ amount, currency, label }] in their original purchase currency.
export function summarizeOriginalCurrencies(units, adjustments = []) {
  const totals = new Map();
  let unknownAmounts = 0;
  const assumptions = [];
  const seen = new Set();
  for (const unit of units) {
    if (seen.has(unit.unit_id)) continue;
    seen.add(unit.unit_id);
    for (const [prefix, field, amount] of [
      ['purchase', 'cost', unit.unit_price], ['order', 'revenue', originalSellPrice(unit)],
    ]) {
      if (amount == null) continue;
      const code = String(unit[`${prefix}_currency`] || '').trim().toUpperCase();
      const raw = String(unit[`${prefix}_currency_raw`] || '').trim().toUpperCase();
      const reason = unit[`${prefix}_currency_review_reason`];
      const missing = !code && (!reason || reason === 'missing');
      const assumed = EVENT_CURRENCIES[unit.twz_location || unit.source_tab];
      const currency = missing ? assumed : /^[A-Z]{3}$/.test(code) ? code
        : unit[`${prefix}_currency_review_reason`] === 'unsupported' && /^[A-Z]{3}$/.test(raw) ? raw : null;
      if (!currency || !Number.isFinite(Number(amount))) {
        unknownAmounts += 1;
        continue;
      }
      if (missing && assumed) {
        assumptions.push(`Currency unknown for ${prefix === 'purchase' ? 'Purchase' : 'Order'} with unit code ${unit.unit_code}; assumed ${assumed} from TWZ Location.`);
      }
      if (!totals.has(currency)) totals.set(currency, { currency, cost: 0, revenue: 0, adjustments: 0 });
      totals.get(currency)[field] += Number(amount);
    }
  }
  for (const adjustment of adjustments) {
    const amount = Number(adjustment.amount);
    if (!amount) continue;
    if (!adjustment.currency || !Number.isFinite(amount)) {
      unknownAmounts += 1;
      continue;
    }
    if (adjustment.assumed) {
      assumptions.push(`Currency unknown for ${adjustment.label || 'adjustment'}; assumed ${adjustment.currency} from TWZ Location.`);
    }
    if (!totals.has(adjustment.currency)) {
      totals.set(adjustment.currency, { currency: adjustment.currency, cost: 0, revenue: 0, adjustments: 0 });
    }
    totals.get(adjustment.currency).adjustments += amount;
  }
  return {
    rows: [...totals.values()].sort((a, b) => a.currency.localeCompare(b.currency))
      .map((row) => ({ ...row, pnl: row.revenue - row.cost + row.adjustments })),
    unknownAmounts,
    assumptions,
  };
}

export const originalSellPrice = (unit) => unit.sell_unit_price ?? (
  unit.sell_total != null && Number(unit.order_quantity) > 0
    ? Number(unit.sell_total) / Number(unit.order_quantity) : null
);

const previousMonth = (month) => {
  const [year, number] = month.split('-').map(Number);
  return number === 1 ? `${year - 1}-12` : `${year}-${String(number - 1).padStart(2, '0')}`;
};

export function convertEventAmount(amount, source, target, transactionDate, snapshot, label, assumedCurrency = target) {
  const issues = [];
  const rawCode = String(source.currency || '').trim().toUpperCase();
  const missing = !rawCode && (!source.reason || source.reason === 'missing');
  const currency = missing ? assumedCurrency : rawCode;
  if (missing) {
    issues.push(assumedCurrency
      ? `${label}: currency unknown${source.raw ? ` (received "${source.raw}")` : ''}; assumed ${assumedCurrency} from TWZ Location.`
      : `${label}: currency unknown and TWZ Location has no default currency; cannot assume a source currency.`);
  }
  let month = /^\d{4}-\d{2}-\d{2}$/.test(transactionDate || '') ? transactionDate.slice(0, 7) : null;
  const result = { value: null, issues, blocked: false, provisional: false, currency, target, rateMonth: null, factor: null };
  if (!SUPPORTED.has(currency)) {
    issues.push(`${label}: currency ${source.raw || currency || 'unknown'} is unsupported; amount cannot be converted to ${target}.`);
    return { ...result, blocked: true };
  }
  if (currency === target) return { ...result, value: Number(amount), factor: 1 };
  if (!month) {
    month = /^\d{4}-\d{2}-\d{2}$/.test(source.createdAt || '') ? source.createdAt.slice(0, 7) : null;
    issues.push(month
      ? `${label}: transaction date is missing or unreadable; using created_at month ${month} for currency conversion.`
      : `${label}: transaction date and created_at are missing or unreadable.`);
  }
  if (!month) return { ...result, blocked: true };
  const currentMonth = snapshot?.current_month;
  if (!currentMonth) {
    issues.push(`${label}: exchange rates are unavailable; cannot convert ${currency} to ${target}.`);
    return { ...result, blocked: true };
  }
  if (month > currentMonth) {
    issues.push(`${label}: transaction date is in a future month (${month}); no exchange rate selected.`);
    return { ...result, blocked: true };
  }
  let rateMonth = month;
  let rates = snapshot.rates?.[rateMonth];
  // An unfinished month always uses the preceding month. Just after month end,
  // retain that same provisional rate until the final monthly observation arrives,
  // even if publication or ingestion is delayed beyond the next calendar month.
  if (month === currentMonth || !rates) {
    rateMonth = previousMonth(month);
    rates = snapshot.rates?.[rateMonth];
    result.provisional = true;
  }
  const sourceRate = currency === 'EUR' ? 1 : Number(rates?.[currency]);
  const targetRate = target === 'EUR' ? 1 : Number(rates?.[target]);
  if (!rates || !(sourceRate > 0) || !(targetRate > 0) || !Number.isFinite(sourceRate) || !Number.isFinite(targetRate)) {
    issues.push(`${label}: exchange rate for ${rateMonth} (${currency} to ${target}) is missing or invalid.`);
    return { ...result, blocked: true };
  }
  if (result.provisional) {
    issues.push(`${label}: provisional conversion for ${month}, using ${rateMonth} ECB rates; the final monthly rate will replace it when available.`);
  }
  const factor = targetRate / sourceRate;
  return { ...result, value: Number(amount) * factor, rateMonth, factor };
}

export function decorateEventUnit(unit, target, snapshot, assumedCurrency = target) {
  if (!target) return unit; // Views outside event conversion retain their existing behavior.
  const convertSide = (amount, prefix, label) => {
    if (amount == null) return { value: null, issues: [], blocked: false };
    return convertEventAmount(amount, {
      currency: unit[`${prefix}_currency`], raw: unit[`${prefix}_currency_raw`],
      reason: unit[`${prefix}_currency_review_reason`],
      createdAt: unit[`${prefix}_created_at`],
    }, target, unit[`${prefix}_date`], snapshot, label, assumedCurrency);
  };
  const buy = convertSide(unit.unit_price, 'purchase', `Purchase ${unit.purchase_id ?? unit.unit_code}`);
  const sell = convertSide(originalSellPrice(unit), 'order', `Sale ${unit.tikwiz_order_id || unit.order_id || unit.unit_code}`);
  return { ...unit, currency_display: { buy, sell, target }, currency_issues: [...buy.issues, ...sell.issues] };
}

export const displayBuy = (unit) => unit?.currency_display ? unit.currency_display.buy.value : unit?.unit_price;
export const displaySell = (unit) => unit?.currency_display ? unit.currency_display.sell.value : (unit ? originalSellPrice(unit) : null);
export const currencyIssues = (units) => [...new Set((units || []).flatMap((unit) => unit.currency_issues || []))];
export const hasBlockedCurrency = (units, side) => (units || []).some((unit) => (
  side ? unit.currency_display?.[side]?.blocked : (unit.currency_display?.buy.blocked || unit.currency_display?.sell.blocked)
));
export const currencyPriceTooltip = (unit, side) => {
  const conversion = unit?.currency_display?.[side];
  if (!conversion) return '';
  const original = side === 'buy' ? unit.unit_price : originalSellPrice(unit);
  if (original == null) return '';
  return `Original: ${original} ${conversion.currency || 'unknown'}. Display: ${conversion.target || unit.currency_display.target}.${conversion.rateMonth ? ` ECB month: ${conversion.rateMonth}${conversion.provisional ? ' (provisional)' : ''}.` : ''} Editing changes the original amount in ${conversion.currency || 'its source currency'}.`;
};

// Adjustments belong to the position's purchases, not its display currency.
export function convertEventAdjustment(amount, units, target, snapshot, assumedCurrency, label) {
  if (!target || !amount) return { value: amount, issues: [], blocked: false };
  const purchases = units.filter((unit) => unit.purchase_id != null && unit.unit_price != null);
  const blocked = (reason) => ({ value: null, blocked: true, issues: [`${label}: ${reason}`] });
  if (!purchases.length) return blocked('no linked purchase is available to determine the adjustment currency and month.');
  const sources = purchases.map((unit) => ({
    currency: unit.purchase_currency, raw: unit.purchase_currency_raw, reason: unit.purchase_currency_review_reason,
    createdAt: unit.purchase_created_at,
  }));
  const currencies = new Set(sources.map((source) => (
    !source.currency && (!source.reason || source.reason === 'missing') ? assumedCurrency : source.currency
  )));
  if (currencies.size > 1) return blocked('linked purchases have different currencies; the adjustment cannot be assigned a single purchase currency.');
  const [currency] = currencies;
  const months = new Set(purchases.map((unit) => (unit.purchase_date || unit.purchase_created_at)?.slice(0, 7) || 'unknown'));
  if (currency !== target && months.size > 1) return blocked('linked purchases span different transaction months; the adjustment needs a specific purchase month for conversion.');
  return convertEventAmount(amount, sources[0], target, purchases[0].purchase_date, snapshot, label, assumedCurrency);
}
